<script setup>
// Aba "Unidade" do negócio (Task 13, módulo Empreendimentos): mostra a
// unidade ligada ao mapping do negócio (mapping.metadata.unit_id) — número,
// torre, tipologia, preço, status e, quando reservada por ESTE negócio, a
// contagem regressiva com ações de prorrogar/liberar. Sem unidade ligada
// (ou com a reserva vencida), oferece o fluxo de ligar: escolher o
// empreendimento, abrir o espelho em modo seleção (só unidades disponíveis
// clicáveis — `SalesMirror`/`UnitCell` prop `selectable`/`disabled`) e
// reservar a unidade escolhida para este negócio.
//
// O backend não expõe "atualizar só o mapping"; a tela do negócio inteira
// só é recarregada via `useDealRecord().load()`, que fica em
// `DealRecordView.vue` (não é repassado pra cá — o contrato combinado pro
// render desta aba é só `:mapping="mapping"`, sem `@update`). Por isso a
// ligação de unidade é refletida guardando o `unit_id` recém-reservado num
// ref local (`linkedUnitId`) — assim a aba mostra a unidade certa sem
// depender de um reload de página. Um `watch` mantém esse ref sincronizado
// pra frente com `mapping.metadata.unit_id` (nunca pra trás: se o prop
// ainda não carregou, o valor setado localmente não é apagado).
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useCurrency } from 'dashboard/composables/useCurrency';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import EmpreendimentosAPI from 'dashboard/api/empreendimentos';
import SalesMirror from 'dashboard/routes/dashboard/empreendimentos/components/SalesMirror.vue';
import { useCountdown } from 'dashboard/routes/dashboard/empreendimentos/composables/useCountdown';

const props = defineProps({
  mapping: { type: Object, required: true },
});

const { t } = useI18n();
const router = useRouter();
const { formatCurrency } = useCurrency();

const isLoading = ref(false);
const isBusy = ref(false);
const unit = ref(null);

const linkedUnitId = ref(null);

const STATUS_CHIP = {
  available: 'bg-n-teal-3 text-n-teal-11',
  reserved: 'bg-n-amber-3 text-n-amber-11',
  sold: 'bg-n-blue-3 text-n-blue-11',
  blocked: 'bg-n-slate-4 text-n-slate-11',
};

const statusChipClass = computed(
  () => STATUS_CHIP[unit.value?.status] || STATUS_CHIP.blocked
);

const reservation = computed(() => unit.value?.reservation || null);
const expiresAt = computed(() => reservation.value?.expires_at);
const { label: countdownLabel, expired: countdownExpired } =
  useCountdown(expiresAt);

// A reserva não guarda sempre `mapping_id` num shape estritamente novo —
// trata ausência de `mapping_id` como "é minha" (o caso normal do dia a dia
// tem o campo preenchido; só o caso extremo de outro negócio ter reservado
// primeiro é que precisa do id batendo E DIFERENTE do nosso).
const isMine = computed(() => {
  const r = reservation.value;
  if (!r) return false;
  if (r.mapping_id == null) return true;
  return String(r.mapping_id) === String(props.mapping.id);
});

const otherReservationLabel = computed(() => {
  const r = reservation.value;
  if (!r) return '';
  if (r.broker_name) {
    return r.brokerage_name
      ? `${r.broker_name} · ${r.brokerage_name}`
      : r.broker_name;
  }
  return r.user_name || '';
});

// Estados mutuamente exclusivos do card, na ordem em que são checados.
const viewState = computed(() => {
  if (!unit.value) return null;
  if (unit.value.status === 'sold') return 'sold';
  if (isMine.value && reservation.value) {
    return countdownExpired.value ? 'expired' : 'mine';
  }
  if (unit.value.status === 'reserved' && !isMine.value) return 'other';
  // `metadata.unit_id` ainda aponta pra cá, mas o backend já liberou a
  // unidade (reserva vencida processada em background) — mesmo tratamento
  // de "vencida", só que sem reservation.
  if (unit.value.status === 'available') return 'expired';
  return null;
});

const handleError = (error, fallbackKey) => {
  if (error?.response?.status === 409) {
    useAlert(t('EMPREENDIMENTOS.UNIT.CONFLICT'));
    return;
  }
  useAlert(
    error?.response?.data?.message ||
      error?.response?.data?.error ||
      t(fallbackKey)
  );
};

const loadUnit = async () => {
  if (!linkedUnitId.value) return;
  isLoading.value = true;
  try {
    const { data } = await EmpreendimentosAPI.unit(linkedUnitId.value);
    unit.value = data;
  } catch (error) {
    handleError(error, 'EMPREENDIMENTOS.ERRORS.LOAD');
  } finally {
    isLoading.value = false;
  }
};

watch(
  () => props.mapping?.metadata?.unit_id,
  value => {
    if (!value) return;
    linkedUnitId.value = value;
    loadUnit();
  },
  { immediate: true }
);

const extend = async () => {
  isBusy.value = true;
  try {
    await EmpreendimentosAPI.extendReservation(reservation.value.id);
    useAlert(t('EMPREENDIMENTOS.UNIT.EXTENDED_OK'));
    await loadUnit();
  } catch (error) {
    handleError(error, 'EMPREENDIMENTOS.ERRORS.SAVE');
    // Estado pode ter mudado por baixo dos nossos pés (ex.: 409 porque a
    // reserva já tinha vencido) — recarrega pra refletir a verdade atual,
    // já que não há um `@updated` do pai pra fazer isso por nós.
    await loadUnit();
  } finally {
    isBusy.value = false;
  }
};

const reasonDialogRef = ref(null);
const reasonText = ref('');

const openReleaseDialog = () => {
  reasonText.value = '';
  reasonDialogRef.value?.open();
};

const handleReleaseConfirm = async () => {
  isBusy.value = true;
  try {
    await EmpreendimentosAPI.release(unit.value.id, reasonText.value);
    useAlert(t('EMPREENDIMENTOS.UNIT.RELEASED_OK'));
    reasonDialogRef.value?.close();
    await loadUnit();
  } catch (error) {
    handleError(error, 'EMPREENDIMENTOS.ERRORS.SAVE');
    await loadUnit();
  } finally {
    isBusy.value = false;
  }
};

const openMirror = () => {
  router.push({
    name: 'empreendimentos_development',
    params: { developmentId: unit.value.development_id },
  });
};

// Fluxo de ligação: escolher empreendimento → espelho em modo seleção →
// reservar a unidade escolhida para este negócio.
const linkDialogRef = ref(null);
const developments = ref([]);
const selectedDevelopmentId = ref('');
const mirror = ref({ towers: [] });
const isLoadingMirror = ref(false);

const openLinkDialog = async () => {
  selectedDevelopmentId.value = '';
  mirror.value = { towers: [] };
  linkDialogRef.value?.open();
  try {
    const { data } = await EmpreendimentosAPI.developments();
    developments.value = data?.payload || [];
  } catch (error) {
    handleError(error, 'EMPREENDIMENTOS.ERRORS.LOAD');
  }
};

watch(selectedDevelopmentId, async id => {
  if (!id) return;
  isLoadingMirror.value = true;
  try {
    const { data } = await EmpreendimentosAPI.mirror(id);
    mirror.value = data;
  } catch (error) {
    handleError(error, 'EMPREENDIMENTOS.ERRORS.LOAD');
  } finally {
    isLoadingMirror.value = false;
  }
});

const handleUnitSelected = async selectedUnit => {
  isBusy.value = true;
  try {
    await EmpreendimentosAPI.reserve(selectedUnit.id, props.mapping.id);
    useAlert(t('EMPREENDIMENTOS.DEAL_TAB.LINKED_OK'));
    linkDialogRef.value?.close();
    linkedUnitId.value = selectedUnit.id;
    await loadUnit();
  } catch (error) {
    handleError(error, 'EMPREENDIMENTOS.ERRORS.SAVE');
    // Se já havia uma unidade ligada (religando após reserva vencida),
    // recarrega pra refletir a verdade atual mesmo com a reserva falhando.
    await loadUnit();
  } finally {
    isBusy.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col gap-4">
    <div
      v-if="isLoading"
      class="flex items-center justify-center py-10 text-n-slate-11"
    >
      <Spinner />
    </div>

    <template v-else-if="!linkedUnitId">
      <p class="text-sm text-n-slate-11">
        {{ t('EMPREENDIMENTOS.DEAL_TAB.NONE') }}
      </p>
      <Button
        size="sm"
        class="self-start"
        :label="t('EMPREENDIMENTOS.DEAL_TAB.LINK')"
        @click="openLinkDialog"
      />
    </template>

    <template v-else-if="unit">
      <div
        class="flex flex-col gap-3 p-4 border rounded-lg border-n-weak bg-n-solid-1"
      >
        <div class="flex items-center justify-between gap-2">
          <h3 class="text-sm font-semibold text-n-slate-12">
            {{ t('EMPREENDIMENTOS.UNIT.TITLE', { number: unit.number }) }}
          </h3>
          <span
            class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium shrink-0"
            :class="statusChipClass"
          >
            {{ t(`EMPREENDIMENTOS.UNIT_STATUS.${unit.status}`) }}
          </span>
        </div>

        <dl class="grid grid-cols-2 gap-3">
          <div class="flex flex-col gap-0.5">
            <dt class="text-xs font-medium text-n-slate-11">
              {{ t('EMPREENDIMENTOS.UNIT.TOWER') }}
            </dt>
            <dd class="text-sm text-n-slate-12">
              {{ unit.tower_name || '—' }}
            </dd>
          </div>
          <div class="flex flex-col gap-0.5">
            <dt class="text-xs font-medium text-n-slate-11">
              {{ t('EMPREENDIMENTOS.UNIT.TYPOLOGY') }}
            </dt>
            <dd class="text-sm text-n-slate-12">
              {{ unit.typology || '—' }}
            </dd>
          </div>
          <div class="flex flex-col gap-0.5">
            <dt class="text-xs font-medium text-n-slate-11">
              {{ t('EMPREENDIMENTOS.UNIT.PRICE') }}
            </dt>
            <dd class="text-sm text-n-slate-12">
              {{ unit.list_price ? formatCurrency(unit.list_price) : '—' }}
            </dd>
          </div>
        </dl>

        <div
          v-if="viewState === 'mine'"
          class="flex flex-col gap-2 p-3 rounded-lg bg-n-amber-9/20 text-n-amber-11"
        >
          <span class="text-xs font-medium">
            {{ t('EMPREENDIMENTOS.DEAL_TAB.COUNTDOWN') }} {{ countdownLabel }}
          </span>
          <div class="flex flex-wrap gap-2">
            <Button
              v-if="!reservation.extended"
              size="sm"
              variant="faded"
              color="amber"
              :is-loading="isBusy"
              :disabled="isBusy"
              :label="t('EMPREENDIMENTOS.UNIT.EXTEND')"
              @click="extend"
            />
            <Button
              size="sm"
              variant="faded"
              color="ruby"
              :is-loading="isBusy"
              :disabled="isBusy"
              :label="t('EMPREENDIMENTOS.UNIT.RELEASE')"
              @click="openReleaseDialog"
            />
          </div>
        </div>

        <p v-else-if="viewState === 'sold'" class="text-sm text-n-slate-11">
          {{ t('EMPREENDIMENTOS.DEAL_TAB.SOLD') }}
        </p>

        <div v-else-if="viewState === 'expired'" class="flex flex-col gap-2">
          <p class="text-sm text-n-slate-11">
            {{ t('EMPREENDIMENTOS.DEAL_TAB.EXPIRED') }}
          </p>
          <Button
            size="sm"
            class="self-start"
            :label="t('EMPREENDIMENTOS.DEAL_TAB.LINK')"
            @click="openLinkDialog"
          />
        </div>

        <p v-else-if="viewState === 'other'" class="text-sm text-n-slate-11">
          {{
            t('EMPREENDIMENTOS.UNIT.RESERVED_BY', {
              name: otherReservationLabel,
            })
          }}
        </p>

        <button
          type="button"
          class="self-start text-xs font-medium text-n-brand hover:underline"
          @click="openMirror"
        >
          {{ t('EMPREENDIMENTOS.UNIT.OPEN_MIRROR') }}
        </button>
      </div>
    </template>

    <Dialog
      ref="linkDialogRef"
      :title="t('EMPREENDIMENTOS.DEAL_TAB.LINK')"
      width="2xl"
      overflow-y-auto
      :show-confirm-button="false"
      :cancel-button-label="t('EMPREENDIMENTOS.FORM.CANCEL')"
    >
      <div class="flex flex-col gap-4">
        <label class="flex flex-col gap-1 text-xs font-medium text-n-slate-11">
          {{ t('EMPREENDIMENTOS.DEAL_TAB.PICK_DEVELOPMENT') }}
          <select
            v-model="selectedDevelopmentId"
            class="px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12"
          >
            <option value="">
              {{ t('EMPREENDIMENTOS.DEAL_TAB.PICK_DEVELOPMENT') }}
            </option>
            <option
              v-for="development in developments"
              :key="development.id"
              :value="String(development.id)"
            >
              {{ development.name }}
            </option>
          </select>
        </label>

        <template v-if="selectedDevelopmentId">
          <p class="text-xs text-n-slate-10">
            {{ t('EMPREENDIMENTOS.DEAL_TAB.PICK_UNIT') }}
          </p>
          <div v-if="isLoadingMirror" class="flex justify-center py-6">
            <Spinner />
          </div>
          <SalesMirror
            v-else
            :mirror="mirror"
            selectable
            @select="handleUnitSelected"
          />
        </template>
      </div>
    </Dialog>

    <Dialog
      ref="reasonDialogRef"
      :title="t('EMPREENDIMENTOS.UNIT.RELEASE')"
      width="sm"
      :is-loading="isBusy"
      :confirm-button-label="t('EMPREENDIMENTOS.UNIT.RELEASE')"
      :cancel-button-label="t('EMPREENDIMENTOS.FORM.CANCEL')"
      @confirm="handleReleaseConfirm"
    >
      <label class="flex flex-col gap-1 text-xs font-medium text-n-slate-11">
        {{ t('EMPREENDIMENTOS.UNIT.REASON') }}
        <input
          v-model="reasonText"
          type="text"
          class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:border-n-brand"
        />
      </label>
    </Dialog>
  </div>
</template>
